import React, { ReactElement, useEffect, useState } from "react";
import { 
  Utensils, Coffee, Hospital, Stethoscope, Pill, Palmtree, 
  Hotel, Waves, Umbrella, Dumbbell, ShoppingCart, ShoppingBag, 
  Landmark, GraduationCap, TreePine, Camera, Wine,
} from "lucide-react";
import { createPoiMarkerUrl } from "@/lib/mapPoiIcons";
import i18n from "@/lib/i18n";

export interface POI {
  name: string;
  category: string;
  position: { lat: number; lng: number };
  icon: string;
  color: string;
  markerUrl?: string; // High-quality SVG map pin
}

export interface POITypeConfig {
  icon: string;        // Fallback or text icon
  iconElement: ReactElement; // Lucide icon for premium SVG generation
  color: string;
  label: string;
  googleType: string;
  keywords?: string[]; // Fallback keywords for broader searches
}

const POI_LABEL_DEFAULTS: Record<string, string> = {
  restaurant: "Restaurante",
  cafe: "Café",
  bar: "Bar",
  hospital: "Hospital",
  clinic: "Clínica",
  pharmacy: "Farmácia",
  resort: "Resort",
  hotel: "Hotel",
  pool: "Piscina",
  beach: "Praia",
  gym: "Ginásio",
  supermarket: "Supermercado",
  shopping_mall: "Centro Comercial",
  bank: "Banco",
  school: "Escola",
  park: "Parque",
  attraction: "Atração Turística",
};

export function getPoiTypeLabel(category: string): string {
  return i18n.t(`poi.category.${category}`, { defaultValue: POI_LABEL_DEFAULTS[category] ?? category });
}

function createPoiConfig(
  category: string,
  config: Omit<POITypeConfig, "label">,
): POITypeConfig {
  return Object.defineProperty(config, "label", {
    enumerable: true,
    get: () => getPoiTypeLabel(category),
  }) as POITypeConfig;
}

/** Categories shown by default across the app. */
export const POI_TYPES: Record<string, POITypeConfig> = {
  restaurant:    createPoiConfig("restaurant", { icon: "🍽️", iconElement: <Utensils />, color: "#E53E3E", googleType: "restaurant" }),
  cafe:          createPoiConfig("cafe", { icon: "☕", iconElement: <Coffee />, color: "#805AD5", googleType: "cafe" }),
  bar:           createPoiConfig("bar", { icon: "🍸", iconElement: <Wine />, color: "#D53F8C", googleType: "bar" }),
  hospital:      createPoiConfig("hospital", { icon: "🏥", iconElement: <Hospital />, color: "#E53E3E", googleType: "hospital" }),
  clinic:        createPoiConfig("clinic", { icon: "⚕️", iconElement: <Stethoscope />, color: "#38B2AC", googleType: "doctor" }),
  pharmacy:      createPoiConfig("pharmacy", { icon: "💊", iconElement: <Pill />, color: "#319795", googleType: "pharmacy" }),
  resort:        createPoiConfig("resort", { icon: "🌴", iconElement: <Palmtree />, color: "#DD6B20", googleType: "lodging", keywords: ["resort"] }),
  hotel:         createPoiConfig("hotel", { icon: "🏨", iconElement: <Hotel />, color: "#9F7AEA", googleType: "lodging" }),
  pool:          createPoiConfig("pool", { icon: "🏊", iconElement: <Waves />, color: "#3182CE", googleType: "tourist_attraction", keywords: ["piscina", "pool"] }),
  beach:         createPoiConfig("beach", { icon: "⛱️", iconElement: <Umbrella />, color: "#D69E2E", googleType: "tourist_attraction", keywords: ["praia", "beach"] }),
  gym:           createPoiConfig("gym", { icon: "💪", iconElement: <Dumbbell />, color: "#2B6CB0", googleType: "gym" }),
  supermarket:   createPoiConfig("supermarket", { icon: "🛒", iconElement: <ShoppingCart />, color: "#38A169", googleType: "supermarket" }),
  shopping_mall: createPoiConfig("shopping_mall", { icon: "🛍️", iconElement: <ShoppingBag />, color: "#D69E2E", googleType: "shopping_mall" }),
  bank:          createPoiConfig("bank", { icon: "🏦", iconElement: <Landmark />, color: "#4A5568", googleType: "bank" }),
  school:        createPoiConfig("school", { icon: "🎓", iconElement: <GraduationCap />, color: "#F6AD55", googleType: "school" }),
  park:          createPoiConfig("park", { icon: "🌳", iconElement: <TreePine />, color: "#2F855A", googleType: "park" }),
  attraction:    createPoiConfig("attraction", { icon: "📸", iconElement: <Camera />, color: "#ED64A6", googleType: "tourist_attraction" }),
};

/**
 * Fan-out nearby POIs so they don't visually pile up on the map.
 * If two POIs are within ~30m of each other, we push them apart on a
 * small radial pattern (~35m). Purely visual — original names/data unchanged.
 */
function spreadClusteredPois(pois: POI[]): POI[] {
  if (pois.length < 2) return pois;
  const MIN_DIST_M = 60;
  const SPREAD_M = 70;
  // ~1 meter in degrees latitude
  const M_TO_DEG_LAT = 1 / 111320;
  const out = pois.map((p) => ({ ...p, position: { ...p.position } }));

  const distMeters = (a: POI, b: POI) => {
    const dLat = (a.position.lat - b.position.lat) * 111320;
    const dLng = (a.position.lng - b.position.lng) * 111320 * Math.cos((a.position.lat * Math.PI) / 180);
    return Math.sqrt(dLat * dLat + dLng * dLng);
  };

  // Group indices that are close to each other
  const visited = new Set<number>();
  for (let i = 0; i < out.length; i++) {
    if (visited.has(i)) continue;
    const cluster = [i];
    for (let j = i + 1; j < out.length; j++) {
      if (visited.has(j)) continue;
      if (distMeters(out[i], out[j]) < MIN_DIST_M) cluster.push(j);
    }
    if (cluster.length > 1) {
      const centerLat = out[i].position.lat;
      const cosLat = Math.cos((centerLat * Math.PI) / 180) || 1;
      cluster.forEach((idx, k) => {
        const angle = (2 * Math.PI * k) / cluster.length;
        const dLat = (Math.sin(angle) * SPREAD_M) * M_TO_DEG_LAT;
        const dLng = (Math.cos(angle) * SPREAD_M) * M_TO_DEG_LAT / cosLat;
        out[idx].position.lat += dLat;
        out[idx].position.lng += dLng;
        visited.add(idx);
      });
    } else {
      visited.add(i);
    }
  }
  return out;
}

interface UseMapPOIsArgs {
  enabled: boolean;
  map: google.maps.Map | null;
  center: { lat: number; lng: number } | null;
  radiusMeters?: number;
  perTypeLimit?: number;
}

/**
 * Fetches nearby POIs around a center, grouped by our category set.
 * Uses the in-browser PlacesService (no extra cost beyond the `places` lib).
 */
export function useMapPOIs({
  enabled,
  map,
  center,
  radiusMeters = 2500,
  perTypeLimit = 4,
}: UseMapPOIsArgs): POI[] {
  const [pois, setPois] = useState<POI[]>([]);

  useEffect(() => {
    if (!enabled || !map || !center) return;
    if (typeof window === "undefined" || !window.google || !window.google.maps?.places) return;

    let cancelled = false;
    const svc = new window.google.maps.places.PlacesService(map);
    const collected: POI[] = [];

    const fetchType = (key: string, cfg: POITypeConfig) =>
      new Promise<void>((resolve) => {
        const request: google.maps.places.PlaceSearchRequest = {
          location: center,
          radius: radiusMeters,
          type: cfg.googleType,
        };
        if (cfg.keywords && cfg.keywords.length > 0) {
          request.keyword = cfg.keywords.join(" OR ");
        }

        svc.nearbySearch(
          request,
          (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
              const markerUrl = createPoiMarkerUrl(cfg.color, cfg.iconElement);
              for (const r of results.slice(0, perTypeLimit)) {
                const loc = r.geometry?.location;
                if (loc) {
                  collected.push({
                    name: r.name || cfg.label,
                    category: key,
                    position: { lat: loc.lat(), lng: loc.lng() },
                    icon: cfg.icon,
                    color: cfg.color,
                    markerUrl,
                  });
                }
              }
            }
            resolve();
          },
        );
      });

    Promise.all(Object.entries(POI_TYPES).map(([k, c]) => fetchType(k, c))).then(() => {
      if (!cancelled) setPois(spreadClusteredPois(collected));
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, map, center?.lat, center?.lng, radiusMeters, perTypeLimit]);

  return pois;
}
